/* eslint-disable no-undef */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { parseFullName } from 'parse-full-name';
import {
  Input,
  Select,
  Button,
  Alert,
  Spin,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import DatePicker from '../DatePicker';
import './patientSearchBar.scss';

const DATE_FORMAT = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];

// Normalize a name for comparison: lowercase, strip punctuation, collapse whitespace.
const normalizeName = (value) => (value || '')
  .toString()
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Classic Levenshtein edit distance between two short strings.
const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 0; i < a.length; i += 1) {
    const curr = [i + 1];
    for (let j = 0; j < b.length; j += 1) {
      const cost = a[i] === b[j] ? 0 : 1;
      curr[j + 1] = Math.min(curr[j] + 1, prev[j + 1] + 1, prev[j] + cost);
    }
    prev = curr;
  }
  return prev[b.length];
};

// 0..1 similarity (1 = identical) between two names after normalization.
export const nameSimilarity = (a, b) => {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na && !nb) return 1;
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  const distance = levenshtein(na, nb);
  return 1 - (distance / Math.max(na.length, nb.length));
};

// Formats a DOB (from either the extracted prefill or an EMR record) to a canonical
// YYYY-MM-DD for equality comparison. Accepts the US formats staff type and the
// ISO shape EMRs return; anything unparseable becomes '' so it never matches.
const DOB_MATCH_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'MM-DD-YYYY', 'M-D-YYYY', 'MMDDYYYY', moment.ISO_8601];
const normalizeDob = (value) => {
  if (!value) return '';
  const parsed = moment(value, DOB_MATCH_FORMATS, true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

// Rank EMR search results against the extracted patient. A name + DOB match is the
// strongest identity signal, so it always sorts first; the rest are ordered by how close
// their first name is to the extracted first name. With no reference name, the EMR's
// original order is kept.
//
// Sorting and labeling are deliberately decoupled. The "Exact match" *label*
// (`isExactMatch`) is narrower than the sort boost: it fires only when
// `exactMatchEligible` is true — i.e. the results came from a Name + DOB search.
// Name-only, DOB-only, MRN, and PMS-ID searches still float a genuine name + DOB match
// to the top (so the likeliest chart isn't buried behind an earlier same-first-name
// row), but withhold the label, since one identifier alone can't substantiate the
// claim. Pure so it can be unit-tested without rendering.
export const rankPatients = (patients, prefill, exactMatchEligible = true) => {
  const withKeys = (patients || []).map((record) => ({ ...record, key: record.mrn || record.id }));

  const extractedFirst = normalizeName(prefill && prefill.firstName);
  const extractedLast = normalizeName(prefill && prefill.lastName);
  const extractedFull = normalizeName(`${(prefill && prefill.firstName) || ''} ${(prefill && prefill.lastName) || ''}`);
  const extractedDob = normalizeDob(prefill && prefill.dob);

  if (!extractedFull) return withKeys;

  const decorated = withKeys.map((record, index) => {
    const parsed = parseFullName(record.name || '');
    const recordFirst = normalizeName(parsed.first);
    const recordLast = normalizeName(parsed.last);
    const recordFull = normalizeName(record.name);
    const nameMatch = recordFull === extractedFull
      || (extractedFirst && extractedLast && recordFirst === extractedFirst && recordLast === extractedLast);
    const dobMatch = Boolean(extractedDob && normalizeDob(record.dob) === extractedDob);
    // Agreeing on name AND DOB is the strongest identity signal: a shared name alone can
    // be a namesake and a shared DOB alone doesn't confirm the person. It drives the sort
    // in every mode, but the visible "Exact match" label additionally requires a Name +
    // DOB search (exactMatchEligible) before we assert the match to the user.
    const strongMatch = Boolean(nameMatch && dobMatch);
    return {
      record: { ...record, isExactMatch: Boolean(exactMatchEligible && strongMatch) },
      index,
      strongMatch,
      firstNameScore: nameSimilarity(parsed.first, prefill && prefill.firstName),
    };
  });

  decorated.sort((a, b) => {
    if (a.strongMatch !== b.strongMatch) return a.strongMatch ? -1 : 1;
    if (b.firstNameScore !== a.firstNameScore) return b.firstNameScore - a.firstNameScore;
    return a.index - b.index; // stable: keep the EMR's order for equal scores
  });

  return decorated.map((item) => item.record);
};

// Shared patient-search bar for the EMR chart modals (Send to Chart / Create New
// Chart). Self-contained: owns its own search state and calls back to the parent
// via onSelectPatient when a result row is chosen. The parent keeps the divergent
// selection logic (it differs per modal/EMR) and the submit-side patient lookup.
//
// When `autoSearchOnMount` is set, it opens by searching the extracted patient's
// last name + DOB (the most reliable identifiers — first names vary by nickname
// or initial). Results are then ranked so an exact full-name match is highlighted
// and listed first, with the remainder ordered by how close their first name is to
// the extracted first name.
class PatientSearchBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchType: props.defaultSearchType,
      searchedPatientFirstName: '',
      searchedPatientMiddleName: '',
      searchedPatientLastName: '',
      searchedPatientDOB: '',
      searchedPatientMRN: '',
      searchedPatientPmsId: '',
      apiPatientsList: [],
      searchRelaxed: false,
      loading: false,
      hasAutoSearched: false,
      // True once a search has completed, so the results area can show an inline
      // "no matches" empty state instead of a top-right toast.
      searchPerformed: false,
      // The search mode that produced the currently-displayed results. Only a
      // Name + DOB search substantiates the "Exact match" highlight, and we key
      // off what was actually searched (not the live dropdown) so changing the
      // mode without re-searching can't retroactively flip the flag on stale rows.
      resultsSearchType: null,
    };
  }

  componentDidMount() {
    this.isUnmounted = false;
    this.maybeAutoSearch();
  }

  componentDidUpdate() {
    // The parent may parse the document's patient name asynchronously, so the
    // prefill (last name + DOB) can arrive after we first mount.
    this.maybeAutoSearch();
  }

  componentWillUnmount() {
    // The auto-search fires on mount and the EMA API can be slow, so a request is
    // often still in flight when the modal closes (e.g. staff submitted the chart
    // before the search finished). Once unmounted, its late response must not
    // setState (React warning) or fire the global "EMA took too long to respond"
    // toastr — a stray error the user can't act on. Flag the unmount and bump the
    // request id so the post-await guard in handleSearchPatient drops the response.
    this.isUnmounted = true;
    this.searchRequestId = (this.searchRequestId || 0) + 1;
  }

  handleSearchTypeChange = (value) => {
    // Preserve what's already entered (name, DOB, MRN) so switching modes — e.g.
    // from "Name & DOB" to "DOB" — carries the values over instead of forcing a
    // retype. The previous results stay until the next search.
    this.setState({ searchType: value });
  };

  getSearchConfig() {
    const {
      searchType,
      searchedPatientMRN,
      searchedPatientPmsId,
      searchedPatientFirstName,
      searchedPatientMiddleName,
      searchedPatientLastName,
      searchedPatientDOB,
    } = this.state;
    const searchedPatientDOBMoment = searchedPatientDOB ? moment(searchedPatientDOB, DATE_FORMAT, true) : null;
    const searchedPatientDOBValue = searchedPatientDOBMoment && searchedPatientDOBMoment.isValid() ? searchedPatientDOBMoment : null;

    const searchConfigs = {
      mrn: {
        input: (
          <Input
            key={`mrn-input-${searchType}`}
            name="searchedPatientMRN"
            placeholder="Patient MRN"
            value={searchedPatientMRN}
            onChange={(e) => this.setState({ searchedPatientMRN: e.target.value })}
            style={{ width: '320px', maxWidth: '100%' }}
            allowClear
          />
        ),
        button: {
          onClick: () => this.handleSearchPatient('mrn'),
          disabled: !searchedPatientMRN,
          text: 'Search',
        },
      },
      pmsId: {
        input: (
          <Input
            key={`pms-input-${searchType}`}
            name="searchedPatientPmsId"
            placeholder="PMS ID"
            value={searchedPatientPmsId}
            onChange={(e) => this.setState({ searchedPatientPmsId: e.target.value })}
            style={{ width: '320px', maxWidth: '100%' }}
            allowClear
          />
        ),
        button: {
          onClick: () => this.handleSearchPatient('pmsId'),
          disabled: !searchedPatientPmsId,
          text: 'Search',
        },
      },
      name: {
        input: (
          <div key={`name-input-${searchType}`} style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <Input
              placeholder="First"
              value={searchedPatientFirstName}
              onChange={(e) => this.setState({ searchedPatientFirstName: e.target.value })}
              style={{ flex: 1 }}
              allowClear
            />
            <Input
              placeholder="Middle"
              value={searchedPatientMiddleName}
              onChange={(e) => this.setState({ searchedPatientMiddleName: e.target.value })}
              style={{ flex: 0.7 }}
              allowClear
            />
            <Input
              placeholder="Last"
              value={searchedPatientLastName}
              onChange={(e) => this.setState({ searchedPatientLastName: e.target.value })}
              style={{ flex: 1 }}
              allowClear
            />
          </div>
        ),
        button: {
          onClick: () => this.handleSearchPatient('name'),
          disabled: !searchedPatientFirstName && !searchedPatientLastName,
          text: 'Search',
        },
      },
      dob: {
        input: (
          <DatePicker
            key={`dob-input-${searchType}`}
            placeholder="Date of Birth"
            onChange={(_, dateString) => this.setState({ searchedPatientDOB: dateString })}
            format={DATE_FORMAT}
            value={searchedPatientDOBValue}
            style={{ width: '220px' }}
          />
        ),
        button: {
          onClick: () => this.handleSearchPatient('dob'),
          disabled: !searchedPatientDOB,
          text: 'Search',
        },
      },
      nameAndDOB: {
        input: (
          <div key={`name-and-dob-input-${searchType}`} style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <Input
              placeholder="First"
              value={searchedPatientFirstName}
              onChange={(e) => this.setState({ searchedPatientFirstName: e.target.value })}
              style={{ flex: 1 }}
              allowClear
            />
            <Input
              placeholder="Middle"
              value={searchedPatientMiddleName}
              onChange={(e) => this.setState({ searchedPatientMiddleName: e.target.value })}
              style={{ flex: 0.7 }}
              allowClear
            />
            <Input
              placeholder="Last"
              value={searchedPatientLastName}
              onChange={(e) => this.setState({ searchedPatientLastName: e.target.value })}
              style={{ flex: 1 }}
              allowClear
            />
            <DatePicker
              onChange={(_, dateString) => this.setState({ searchedPatientDOB: dateString })}
              format={DATE_FORMAT}
              value={searchedPatientDOBValue}
              placeholder="Date of Birth"
              style={{ flex: 1 }}
            />
          </div>
        ),
        button: {
          onClick: () => this.handleSearchPatient('nameAndDOB'),
          disabled: !((searchedPatientFirstName || searchedPatientLastName) && searchedPatientDOB),
          text: 'Search',
        },
      },
    };

    return searchConfigs[this.state.searchType];
  }

  getRankedDataSource() {
    // Only a Name + DOB search can substantiate an "Exact match" highlight; name-only,
    // DOB-only, MRN, and PMS-ID searches still rank results but never flag one as exact.
    const exactMatchEligible = this.state.resultsSearchType === 'nameAndDOB';
    return rankPatients(this.state.apiPatientsList, this.props.prefill, exactMatchEligible);
  }

  // Pre-populate last name + DOB and search once when the modal opens.
  maybeAutoSearch = () => {
    const { autoSearchOnMount, prefill } = this.props;
    if (!autoSearchOnMount || this.state.hasAutoSearched) return;

    const lastName = ((prefill && prefill.lastName) || '').trim();
    const dob = ((prefill && prefill.dob) || '').trim();
    if (!lastName || !dob) return;

    // Open on the broadest reliable identifiers — last name + DOB — and leave the
    // first name out of the query. Ranking surfaces the closest first-name match.
    // The fields stay populated with what was searched so the user can see and
    // tweak the query (e.g. add a first name) rather than retyping it.
    this.setState({
      hasAutoSearched: true,
      searchType: 'nameAndDOB',
      searchedPatientFirstName: '',
      searchedPatientMiddleName: '',
      searchedPatientLastName: lastName,
      searchedPatientDOB: dob,
    }, () => this.handleSearchPatient('nameAndDOB'));
  };

  handleSearchPatient = async (type) => {
    const { getEmaPatients, getTebraPatients, isKareo } = this.props;
    const {
      searchedPatientDOB, searchedPatientMRN, searchedPatientPmsId, searchedPatientFirstName, searchedPatientMiddleName, searchedPatientLastName,
    } = this.state;

    let searchQuery = '';
    if (type === 'mrn') {
      searchQuery = `?mrn=${encodeURIComponent(searchedPatientMRN)}`;
    } else if (type === 'pmsId') {
      searchQuery = `?pms_id=${encodeURIComponent(searchedPatientPmsId.trim())}`;
    } else if (type === 'name') {
      const params = [];
      if (searchedPatientFirstName) {
        params.push(`first_name=${encodeURIComponent(searchedPatientFirstName.trim())}`);
      }
      if (searchedPatientLastName) {
        params.push(`last_name=${encodeURIComponent(searchedPatientLastName.trim())}`);
      }
      if (searchedPatientMiddleName) {
        params.push(`middle_name=${encodeURIComponent(searchedPatientMiddleName.trim())}`);
      }
      searchQuery = `?${params.join('&')}`;
    } else if (type === 'dob') {
      searchQuery = `?dob=${encodeURIComponent(searchedPatientDOB.trim())}`;
    } else {
      const params = [];
      if (searchedPatientFirstName) {
        params.push(`first_name=${encodeURIComponent(searchedPatientFirstName.trim())}`);
      }
      if (searchedPatientLastName) {
        params.push(`last_name=${encodeURIComponent(searchedPatientLastName.trim())}`);
      }
      if (searchedPatientMiddleName) {
        params.push(`middle_name=${encodeURIComponent(searchedPatientMiddleName.trim())}`);
      }
      if (searchedPatientDOB) {
        params.push(`dob=${encodeURIComponent(searchedPatientDOB.trim())}`);
      }
      searchQuery = `?${params.join('&')}`;
    }

    this.setState({ loading: true, searchRelaxed: false });

    // Request-identity guard: own the response instead of reading the shared Redux
    // apiPatients slot. The EMA search runs up to three sequential fallback tiers, so
    // a slower earlier request could otherwise resolve last and overwrite a newer
    // one — binding staff to the wrong patient. Only the latest request applies.
    this.searchRequestId = (this.searchRequestId || 0) + 1;
    const requestId = this.searchRequestId;

    // Call the appropriate API based on EMR type and consume its response directly
    // (crud resolves to the axios response, or { error: true, ... } on failure).
    const response = isKareo
      ? await getTebraPatients(searchQuery)
      : await getEmaPatients(searchQuery);

    // A newer search superseded this one, or the component unmounted while the
    // request was in flight — drop the stale response entirely (no setState, no toast).
    if (this.isUnmounted || requestId !== this.searchRequestId) return;

    if (!response || response.error) {
      this.setState({ loading: false });
      toastr.error(response?.response?.data?.error || 'Unable to search for patients. Please try again.');
      return;
    }

    const data = response.data || {};
    this.setState({
      apiPatientsList: data.patients || [],
      // Set when the EMA search only found results after relaxing the name
      // constraints (DOB-anchored fallback) — Tebra responses never set it
      searchRelaxed: Boolean(data.relaxed),
      // A completed search with zero results surfaces inline in the results
      // area rather than as a top-right toast.
      searchPerformed: true,
      // Remember how these results were fetched so the "Exact match" highlight
      // only fires for a Name + DOB search (see getRankedDataSource).
      resultsSearchType: type,
      loading: false,
    });
  };

  formatDob = (dob) => {
    if (!dob) return '';
    const parsed = moment(dob, ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM-DD-YYYY', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : dob;
  };

  renderManualControls() {
    const { isKareo, gatewayEmaSearch } = this.props;
    const config = this.getSearchConfig();
    return (
      <div className="patient-search__controls">
        <div className="patient-search__controls-head">
          <div className="patient-search__mode">
            <span className="patient-search__mode-label">Search by</span>
            <Select
              size="small"
              className="patient-search__mode-select"
              value={this.state.searchType}
              onChange={this.handleSearchTypeChange}
            >
              {/* Non-API EMA searches via the EHR-Gateway scraper, which only accepts
                  MRN or DOB+name — Name-only, DOB-only and PMS ID would return empty,
                  so hide them in that mode. */}
              {!gatewayEmaSearch && <Select.Option value="name">Name</Select.Option>}
              {!gatewayEmaSearch && <Select.Option value="dob">DOB</Select.Option>}
              {!isKareo && <Select.Option value="mrn">MRN</Select.Option>}
              {!isKareo && !gatewayEmaSearch && <Select.Option value="pmsId">PMS ID</Select.Option>}
              <Select.Option value="nameAndDOB">Name &amp; DOB</Select.Option>
            </Select>
          </div>
          <Button
            className="patient-search__btn"
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={config.button.onClick}
            disabled={config.button.disabled}
          >
            Search
          </Button>
        </div>
        <div className="patient-search__fields">
          {config.input}
        </div>
      </div>
    );
  }

  renderResults() {
    const { onSelectPatient, isKareo } = this.props;
    const ranked = this.getRankedDataSource();
    return (
      <div className="patient-search__results">
        <div className="patient-search__results-head">
          {`${ranked.length} ${ranked.length === 1 ? 'match' : 'matches'} found`}
        </div>
        {ranked.map((record) => (
          <button
            type="button"
            key={record.key}
            className={`patient-search__result ${record.isExactMatch ? 'patient-search__result--best' : ''}`}
            onClick={() => onSelectPatient(record)}
          >
            <span className="patient-search__result-top">
              <span className="patient-search__result-name">{record.name}</span>
              {record.isExactMatch && <span className="patient-search__result-tag">Exact match</span>}
            </span>
            <span className="patient-search__result-meta">
              <span>{`DOB ${this.formatDob(record.dob)}`}</span>
              {!isKareo && record.mrn && <span>{`MRN ${record.mrn}`}</span>}
              {!isKareo && record.pms_id && <span>{`PMS ID ${record.pms_id}`}</span>}
            </span>
          </button>
        ))}
      </div>
    );
  }

  render() {
    const { isKareo, showRelaxedAlert } = this.props;
    const {
      loading, apiPatientsList, searchRelaxed, searchPerformed,
    } = this.state;
    const emrName = isKareo ? 'Tebra' : 'ModMed';
    const hasResults = apiPatientsList.length > 0;

    return (
      <div className="patient-search">
        {this.renderManualControls()}

        {loading && (
          <div className="patient-search__loading">
            <Spin size="small" />
            <span>{`Searching ${emrName}…`}</span>
          </div>
        )}

        {showRelaxedAlert && searchRelaxed && hasResults && (
          <Alert
            type="warning"
            showIcon
            message="No exact name match"
            description="Showing patients with the same date of birth. The name on the document may differ from the chart — verify the patient before selecting."
          />
        )}

        {hasResults && this.renderResults()}

        {!hasResults && searchPerformed && !loading && (
          <div className="patient-search__empty">
            <span>{`No matching patients found in ${emrName}.`}</span>
            <span>Try a different name or date of birth.</span>
          </div>
        )}
      </div>
    );
  }
}

PatientSearchBar.defaultProps = {
  isKareo: false,
  gatewayEmaSearch: false,
  showRelaxedAlert: false,
  defaultSearchType: 'name',
  autoSearchOnMount: false,
  prefill: null,
};

PatientSearchBar.propTypes = {
  getEmaPatients: PropTypes.func.isRequired,
  getTebraPatients: PropTypes.func.isRequired,
  isKareo: PropTypes.bool,
  gatewayEmaSearch: PropTypes.bool,
  showRelaxedAlert: PropTypes.bool,
  defaultSearchType: PropTypes.string,
  autoSearchOnMount: PropTypes.bool,
  prefill: PropTypes.shape({
    firstName: PropTypes.string,
    middleName: PropTypes.string,
    lastName: PropTypes.string,
    dob: PropTypes.string,
  }),
  onSelectPatient: PropTypes.func.isRequired,
};

export default PatientSearchBar;
